import { UserGeneratedContentUI } from "./UserGeneratedContentUI";
import { StandardProps } from "../componentMap";
import Script from "next/script";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { useEffect } from "react";

const USER_CONTENT_CLIENT_ID = "web-pdp-scaffold-ui";

type PrefetchUGCParams = {
  apiURL: string;
  productId: string;
  apiKey: string;
};

type UGCResponse = {
  assets: unknown[];
};

/**
 * prefetch UGC API/cache.
 */
export const prefetchUserGeneratedContent = async ({
  apiURL,
  productId,
  apiKey,
}: PrefetchUGCParams): Promise<UGCResponse | undefined> => {
  if (!productId || !apiKey) return;

  const requestUrl = `${apiURL}/v1/content?productId=${encodeURIComponent(productId)}&api_key=${encodeURIComponent(apiKey)}`;

  let response;

  try {
    response = await fetch(requestUrl, {
      method: "GET",
      keepalive: true,
    });
  } catch (e) {
    return;
  }

  if (!response.ok) return;

  return response.json();
};

export function UserGeneratedContent({
  product,
  runtimeConfig,
  experiencesRawObject,
  flags,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "experiencesRawObject" | "flags"
>) {
  const showUGC = flags.showUGC.value === true;
  const { monetateId } = useMonetateId();
  const fireManagedImpression = (value: boolean) => {
    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject,
      "pdp-scaffold-observeUGC",
      value ? "true" : "false",
      monetateId,
    );
  };

  const apiURL = runtimeConfig.userGeneratedContentApiBaseURL;
  const productURL = product?.pdpURL.url;
  const productId = product?.aliases.pimProductId;
  const apiKey = runtimeConfig.userGeneratedContentApiKey;

  useEffect(() => {
    prefetchUserGeneratedContent({
      apiURL,
      productId,
      apiKey,
    }).then((response: UGCResponse | undefined) => {
      if (showUGC && response !== undefined && response?.assets.length > 0) {
        fireManagedImpression(true);
      }
    });
  }, []);

  if (!showUGC) {
    return;
  }

  return (
    <>
      <Script
        src="/user-content-ui/static/js/jl-user-content.js?includeCoreAssets=false"
        type="text/javascript"
        defer
      />
      <UserGeneratedContentUI
        productId={productId}
        productURL={productURL}
        apiKey={apiKey}
        clientId={USER_CONTENT_CLIENT_ID}
        experiencesRawObject={experiencesRawObject}
        isBrandPromise={true}
      />
    </>
  );
}
